import { useEffect, useState } from 'react';
import { useStore } from 'src/contexts/StoreContext';
import { ConfigurationStepId, ConfigurationStepFlavor, getNextStep } from 'src/helpers/configurationSteps.helper';
import { StepInfo } from './ModalConfigurationSteps.types';
import { GROUP_CODES } from 'src/constants/code.constants';
import { DynamicModalProps } from '../DynamicModal.types';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';

export const useModalConfigurationStepsLogic = ({
  dynamicModalVisible,
  dynamicModalLayer: { initialProductConfigurationId, skipConfigurationSteps, groupCode, id, groupId },
}: DynamicModalProps) => {
  const {
    i18n,
    project,
    dynamicModal: { closeModal },
    bottomSheet: { openBottomSheet },
  } = useStore();
  const [actualStep, setActualStep] = useState<ConfigurationStepId>();
  const [cards, setCards] = useState<StepInfo[]>([]);
  const [stepsDone, setStepsDone] = useState<ConfigurationStepId[]>([]);
  const [showInfoPage, setShowInfoPage] = useState<boolean>(false);
  const [showCtaInfo, setShowCtaInfo] = useState<boolean>(false);
  const [prodConfIdStack, setProdConfIdStack] = useState<string[]>([]);
  const [productConfigurationId, setProductConfigurationId] = useState<string>(initialProductConfigurationId);
  const productGroup = groupCode as GROUP_CODES;
  const stepsToSkip = skipConfigurationSteps as ConfigurationStepId[];

  useEffect(() => {
    goToNextStep([]);
  }, []);

  useEffect(() => {
    if (actualStep) {
      amplitude.trackEvent(AnalyticsEvents.lookup.view, {
        lookupcode: actualStep,
      });
    }
  }, [actualStep]);

  const goBack = async (alreadyDoneSteps: ConfigurationStepId[]) => {
    const newStack = [...prodConfIdStack.slice(0, prodConfIdStack.length - 1)];
    const newProductConfigurationId = newStack[newStack.length - 1];

    const nextStep = getNextStep(productGroup, alreadyDoneSteps, stepsToSkip);
    setProdConfIdStack([...prodConfIdStack.slice(0, prodConfIdStack.length - 1)]);

    if (nextStep) {
      const newCards = await getCardsByStep(nextStep.flavor, newProductConfigurationId);
      setCards(newCards);
      setShowCtaInfo(
        [ConfigurationStepFlavor.ALL_FORMAT, ConfigurationStepFlavor.FORMAT_BY_BAND].includes(nextStep.flavor),
      );
      setActualStep(nextStep.id);

      amplitude.trackEvent(AnalyticsEvents.lookup.backClick, {
        lookupcode: actualStep,
        position: actualStep,
        target: nextStep.id,
      });
    }
  };

  const goToNextStep = async (alreadyDoneSteps: ConfigurationStepId[], id?: string) => {
    setProdConfIdStack([...prodConfIdStack, id ?? productConfigurationId]);
    const nextStep = getNextStep(productGroup, alreadyDoneSteps, stepsToSkip);
    if (nextStep) {
      const newCards = await getCardsByStep(nextStep.flavor, id);
      console.log('#[ModalConfigurationSteps] getCardsByStep', { 'nextStep.flavor': nextStep.flavor, id }, newCards);
      setCards(newCards);
      setShowCtaInfo(
        [ConfigurationStepFlavor.ALL_FORMAT, ConfigurationStepFlavor.FORMAT_BY_BAND].includes(nextStep.flavor),
      );
      setActualStep(nextStep.id);
    } else {
      project.activeGroup?.checkMinMaxProductsAndNotify();
      project.addProductsV2({
        ids: project.activeProject?.selectedThumbnails.map(t => t.originalImageId) ?? [],
        productConfigurationId: id ?? productConfigurationId,
        groupCode,
        groupId,
      });
      closeModal();
      // remove skipConfigurationSteps from params
      const url = new URL(window.location.href);
      url.searchParams.delete('skipConfigurationSteps');
      history.pushState(null, '', url);

      project.activeProject?.deselectAllThumbnailImages();
    }
  };

  const onGoBack = async () => {
    const alreadyDoneSteps = [...stepsDone.slice(0, stepsDone.length - 1)];
    setStepsDone(alreadyDoneSteps);
    goBack(alreadyDoneSteps);
  };

  const onConfirmStep = async (id?: string) => {
    amplitude.trackEvent(AnalyticsEvents.lookup.continueClick, {
      lookupcode: actualStep,
      lookupId: cards?.find(card => card.productConfigurationId === productConfigurationId)?.code,
    });
    const alreadyDoneSteps = [...stepsDone, actualStep!];
    setStepsDone(alreadyDoneSteps);
    goToNextStep(alreadyDoneSteps, id);
  };

  const onSelectCard = (id: string) => {
    setProductConfigurationId(id);
  };

  const onSelectCardMobile = (id: string) => {
    setProductConfigurationId(id);
    onConfirmStep(id);
  };

  const getCardsByStep = async (stepFlavor: ConfigurationStepFlavor, prodConfId?: string): Promise<StepInfo[]> => {
    switch (stepFlavor) {
      case ConfigurationStepFlavor.BAND:
        return project.activeProject?.getBands(prodConfId ?? productConfigurationId) ?? [];
      case ConfigurationStepFlavor.ALL_FORMAT:
        return (
          project.activeProject?.getAllFormats(
            productGroup,
            project.activeProject?.selectedThumbnails.map(t => t.originalImageId),
          ) ?? []
        );
      case ConfigurationStepFlavor.FORMAT_BY_BAND:
        return (
          project.activeProject?.getSuggestedFormats(
            productGroup,
            prodConfId ?? productConfigurationId,
            project.activeProject?.selectedThumbnails.map(t => t.originalImageId),
          ) ?? []
        );
      case ConfigurationStepFlavor.BOX_STYLE:
        return project.activeProject?.getBoxStyles(prodConfId ?? productConfigurationId, productGroup) ?? [];
      default:
        return [];
    }
  };

  return {
    i18n,
    dynamicModalVisible,
    closeModal,
    showInfoPage,
    setShowInfoPage,
    actualStep,
    cards,
    onSelectCard,
    onSelectCardMobile,
    showCtaInfo,
    onConfirmStep,
    onGoBack,
    stepsDone,
    id,
    openBottomSheet,
    productGroup,
  };
};
